import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { planCosechaAPI, handleAPIError } from '../../services/api';

export const fetchPlanCosecha = createAsyncThunk(
  'planCosecha/fetchPlanCosecha',
  async ({ temporadaId, week }, { rejectWithValue }) => {
    try {
      const response = await planCosechaAPI.getPlan({ temporadaId, week });
      return response?.data || null;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const createPlanCosecha = createAsyncThunk(
  'planCosecha/createPlanCosecha',
  async ({ temporadaId, seasonYear, week }, { rejectWithValue }) => {
    try {
      const response = await planCosechaAPI.createFromOrders({ temporadaId, seasonYear, week });
      return response?.data || null;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const savePlanCosecha = createAsyncThunk(
  'planCosecha/savePlanCosecha',
  async ({ planId, detalles, removedDetailIds }, { rejectWithValue }) => {
    try {
      const response = await planCosechaAPI.savePlan({ planId, detalles, removedDetailIds });
      return response?.data || null;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const deletePlanCosecha = createAsyncThunk(
  'planCosecha/deletePlanCosecha',
  async ({ temporadaId, semana }, { rejectWithValue }) => {
    try {
      await planCosechaAPI.deletePlan({ temporadaId, semana });
      return null;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

const initialState = {
  plan: null,
  loading: false,
  error: null,
  creating: false,
  saving: false,
  deleting: false,
};

const planCosechaSlice = createSlice({
  name: 'planCosecha',
  initialState,
  reducers: {
    clearPlanCosechaError: (state) => {
      state.error = null;
    },
    resetPlanCosechaState: (state) => {
      state.plan = null;
      state.loading = false;
      state.error = null;
      state.creating = false;
      state.saving = false;
      state.deleting = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlanCosecha.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlanCosecha.fulfilled, (state, action) => {
        state.loading = false;
        state.plan = action.payload;
      })
      .addCase(fetchPlanCosecha.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.plan = null;
      })
      .addCase(createPlanCosecha.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createPlanCosecha.fulfilled, (state, action) => {
        state.creating = false;
        state.plan = action.payload;
      })
      .addCase(createPlanCosecha.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload;
      })
      .addCase(savePlanCosecha.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(savePlanCosecha.fulfilled, (state, action) => {
        state.saving = false;
        state.plan = action.payload;
      })
      .addCase(savePlanCosecha.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(deletePlanCosecha.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deletePlanCosecha.fulfilled, (state) => {
        state.deleting = false;
        state.plan = null;
      })
      .addCase(deletePlanCosecha.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearPlanCosechaError, resetPlanCosechaState } = planCosechaSlice.actions;

export const selectPlanCosecha = (state) => state.planCosecha.plan;
export const selectPlanCosechaLoading = (state) => state.planCosecha.loading;
export const selectPlanCosechaError = (state) => state.planCosecha.error;
export const selectPlanCosechaCreating = (state) => state.planCosecha.creating;
export const selectPlanCosechaSaving = (state) => state.planCosecha.saving;
export const selectPlanCosechaDeleting = (state) => state.planCosecha.deleting;

export default planCosechaSlice.reducer;
